// src/components/Navbar.jsx
import React, { useState, memo } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoIcon from "@mui/icons-material/Info";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import logo from "../assets/images/logo.png";

/* ===== Палитра под логотип (фиолетовый + голубой) ===== */
const NEON = {
  violet: "#c084fc",
  violetSoft: "rgba(192,132,252,0.7)",
  blue: "#38bdf8",
  blueSoft: "rgba(56,189,248,0.7)",
};

const GLASS = {
  bg: "linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(9,9,11,0.98) 100%)",
  border: "1px solid rgba(148,163,184,.45)",
  borderSoft: "1px solid rgba(148,163,184,.32)",
  shadow: "0 18px 40px rgba(15,23,42,.85)",
  text: "#e5e7eb",
  subtext: "rgba(148,163,184,.95)",
  brandGrad: `linear-gradient(90deg, ${NEON.violet} 0%, ${NEON.blue} 100%)`,
};

/* ===== Кнопка Launch (чёрное стекло с неоном) ===== */
const GlassLaunchButton = styled(Button)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 999,
  fontFamily: "'Bruno Ace SC', sans-serif",
  textTransform: "none",
  padding: theme.spacing(1.1, 2.6),
  minHeight: 38,
  color: GLASS.text,
  background:
    "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(6,9,14,0.96) 100%)",
  border: GLASS.border,
  backdropFilter: "blur(14px) saturate(150%)",
  WebkitBackdropFilter: "blur(14px) saturate(150%)",
  boxShadow:
    "0 14px 32px rgba(15,23,42,0.9), inset 0 1px 0 rgba(255,255,255,0.04)",
  transition:
    "transform .25s ease, box-shadow .25s ease, border-color .25s ease, background .25s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    background:
      "linear-gradient(180deg, rgba(15,23,42,1) 0%, rgba(3,7,18,1) 100%)",
    borderColor: NEON.violetSoft,
    boxShadow:
      "0 18px 40px rgba(15,23,42,0.96), 0 0 18px rgba(192,132,252,0.55)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow:
      "0 10px 26px rgba(15,23,42,0.85), inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  "&:focus-visible": {
    outline: "none",
    boxShadow:
      "0 0 0 3px rgba(192,132,252,0.65), 0 12px 30px rgba(15,23,42,0.9)",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    left: "-40%",
    top: 0,
    width: "40%",
    height: "100%",
    transform: "skewX(-20deg)",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)",
    transition: "left .55s ease",
    pointerEvents: "none",
  },
  "&:hover::before": { left: "140%" },

  "& .MuiButton-endIcon": {
    marginLeft: theme.spacing(1),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.9, 2),
    minHeight: 34,
    borderRadius: 16,
  },
}));

/* ===== Логотип в неоновой рамке ===== */
const LogoShell = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1.2),
  borderRadius: 20,
  background:
    "radial-gradient(circle at 0% 0%, rgba(192,132,252,0.35), transparent 60%), radial-gradient(circle at 100% 100%, rgba(56,189,248,0.35), transparent 60%), rgba(15,23,42,0.95)",
  boxShadow:
    "0 0 0 1px rgba(15,23,42,1), 0 18px 40px rgba(15,23,42,0.95), 0 0 20px rgba(15,23,42,1)",
  border: GLASS.border,
  cursor: "pointer",
}));

/* ===== Центр: ссылки ===== */
const NavLinkButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontFamily: "'Bruno Ace SC', sans-serif",
  fontSize: "0.85rem",
  letterSpacing: "0.12em",
  padding: theme.spacing(0.8, 2),
  borderRadius: 999,
  color: GLASS.text,
  opacity: 0.82,
  backgroundColor: "transparent",
  "&:hover": {
    opacity: 1,
    background:
      "radial-gradient(circle at 0 0, rgba(192,132,252,0.32), transparent 55%)",
  },
}));

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLaunchApp = () => {
    window.location.href = "https://app.promospay.ai";
  };

  /* ===== Мобильное меню (drawer) ===== */
  const MobileDrawer = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: "82vw",
          maxWidth: 360,
          borderTopLeftRadius: 18,
          borderBottomLeftRadius: 18,
          background: GLASS.bg,
          borderLeft: GLASS.borderSoft,
          boxShadow:
            "0 24px 60px rgba(15,23,42,0.95), 0 0 0 1px rgba(15,23,42,1)",
          color: GLASS.text,
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: GLASS.borderSoft,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <img
            src={logo}
            alt="PromosPay"
            style={{ height: 32, width: "auto", objectFit: "contain" }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
            }}
          >
            Menu
          </Typography>
        </Box>
        <IconButton
          onClick={() => setMobileMenuOpen(false)}
          sx={{ color: GLASS.text }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ p: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href="https://docs.promospay.ai"
            onClick={() => setMobileMenuOpen(false)}
          >
            <DescriptionIcon
              fontSize="small"
              style={{ marginRight: 12, color: NEON.blueSoft }}
            />
            <ListItemText
              primary="DOCS"
              primaryTypographyProps={{
                sx: {
                  fontFamily: "'Bruno Ace SC', sans-serif",
                  letterSpacing: "0.16em",
                  fontSize: "0.78rem",
                },
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/about"
            onClick={() => setMobileMenuOpen(false)}
          >
            <InfoIcon
              fontSize="small"
              style={{ marginRight: 12, color: NEON.violetSoft }}
            />
            <ListItemText
              primary="PROJECT"
              primaryTypographyProps={{
                sx: {
                  fontFamily: "'Bruno Ace SC', sans-serif",
                  letterSpacing: "0.16em",
                  fontSize: "0.78rem",
                },
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/features"
            onClick={() => setMobileMenuOpen(false)}
          >
            <AutoAwesomeIcon
              fontSize="small"
              style={{ marginRight: 12, color: NEON.blueSoft }}
            />
            <ListItemText
              primary="FUNCTION"
              primaryTypographyProps={{
                sx: {
                  fontFamily: "'Bruno Ace SC', sans-serif",
                  letterSpacing: "0.16em",
                  fontSize: "0.78rem",
                },
              }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1.5, borderColor: "rgba(148,163,184,.45)" }} />

        {/* Glass button at the bottom of the menu*/}
        <Box sx={{ p: 2 }}>
          <GlassLaunchButton
            fullWidth
            onClick={() => {
              setMobileMenuOpen(false);
              handleLaunchApp();
            }}
            endIcon={<OpenInNewIcon />}
          >
            Launch App
          </GlassLaunchButton>
        </Box>
      </List>
    </Drawer>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          color: GLASS.text,
          zIndex: 900,
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 72, sm: 84, md: 96 },
            px: { xs: 1, sm: 2 },
            py: { xs: 1, sm: 1.4 },
            justifyContent: "center",
          }}
        >
          {/* Стеклянная «капсула» навбара */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", lg: 1180 },
              mx: "auto",
              borderRadius: { xs: 16, sm: 18, md: 20 },
              px: { xs: 1.2, sm: 2.2, md: 2.6 },
              py: { xs: 0.6, sm: 0.8 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: GLASS.bg,
              border: GLASS.border,
              boxShadow: GLASS.shadow,
              backdropFilter: "blur(16px) saturate(160%)",
              WebkitBackdropFilter: "blur(16px) saturate(160%)",
            }}
          >
            {/* LEFT: logo */}
            <LogoShell onClick={() => navigate("/")}>
              <img
                src={logo}
                alt="PromosPay Logo"
                style={{
                  height: isMobile ? 40 : 52,
                  width: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </LogoShell>

            {/* CENTER: desktop links */}
            {!isMobile && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.5,
                  ml: 2,
                }}
              >
                <NavLinkButton
                  component="a"
                  href="https://docs.promospay.ai"
                >
                  DOCS
                </NavLinkButton>
                <NavLinkButton component={Link} to="/about">
                  PROJECT
                </NavLinkButton>
                <NavLinkButton component={Link} to="/features">
                  FUNCTION
                </NavLinkButton>
              </Box>
            )}

            {/* RIGHT */}
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <GlassLaunchButton
                onClick={handleLaunchApp}
                endIcon={<OpenInNewIcon />}
              >
                Launch App
              </GlassLaunchButton>

              {isMobile && (
                <>
                  <IconButton
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="open menu"
                    sx={{ ml: 0.5, color: GLASS.text }}
                  >
                    <MenuIcon />
                  </IconButton>
                  {MobileDrawer}
                </>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default memo(Navbar);
